import React, { useMemo } from 'react';
import { Tag, Tooltip, Popover } from 'antd';
import { useTranslation } from 'react-i18next';
import { HtmlContent } from '@client/utils/html_content';
import { STEPS, APPLICATION_TYPES } from '../constants';

/**
 * SelectedValuesTags Component
 * Displays selected values as read-only Tags under each step title
 * Shows formatted values with unit labels from enrichedData
 *
 * Step 1 (Sprayer): "Capacité maximale du réservoir : {value} {unit}"
 * Step 2 (Area): "Superficie totale : {value} {unit}", "% de la superficie totale : {percentage} %"
 * Step 3 (Treatment): "Volume d'eau : {value} {unit}", product dose list
 *
 * @param {Object} props
 * @param {number} props.step - Step number (1-3)
 * @param {Object} props.selections - All selections object (from URL state)
 * @param {Object} props.enrichedData - Enriched data with unit labels
 * @param {number} [props.maxVisible=2] - Maximum number of tags to show before "+N"
 */
// Unit names may be stored as HTML (e.g. "<p>g / ha</p>"); tag labels render on a
// single line, so strip HTML tags and collapse whitespace before interpolating.
const toPlainText = (html) =>
  (html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const SelectedValuesTags = React.memo(
  ({ step, selections, enrichedData = {}, maxVisible = 2 }) => {
    const { t } = useTranslation('sprayCalculator');

    // Get display values for the current step
    const displayValues = useMemo(() => {
      switch (step) {
        case STEPS.SPRAYER: {
          const volume = selections.tankVolume;
          if (!volume) return [];

          const unitName = toPlainText(enrichedData.tankVolumeUnitName);
          return [
            {
              key: 'tank-volume',
              label: `${t('sprayer_volume_label')} : ${volume}${unitName ? ` ${unitName}` : ''}`,
            },
          ];
        }

        case STEPS.AREA: {
          const area = selections.totalArea;
          if (!area) return [];

          const tags = [];
          const unitName = toPlainText(enrichedData.areaUnitName);

          tags.push({
            key: 'total-area',
            label: `${t('area_total_label')} : ${area}${unitName ? ` ${unitName}` : ''}`,
          });

          // Show application type info
          if (selections.applicationType === APPLICATION_TYPES.BAND) {
            if (selections.bandPercentage) {
              tags.push({
                key: 'band-percentage',
                label: `${t('area_band_percentage_label')} : ${selections.bandPercentage} %`,
              });
            } else if (selections.rowSpacing && selections.bandWidth) {
              const rowUnitName = toPlainText(enrichedData.rowSpacingUnitName);
              const bandUnitName = toPlainText(enrichedData.bandWidthUnitName);
              tags.push({
                key: 'row-spacing',
                label: `${t('area_band_row_spacing_label')} : ${selections.rowSpacing}${rowUnitName ? ` ${rowUnitName}` : ''}`,
              });
              tags.push({
                key: 'band-width',
                label: `${t('area_band_width_label')} : ${selections.bandWidth}${bandUnitName ? ` ${bandUnitName}` : ''}`,
              });
            }
          }

          return tags;
        }

        case STEPS.TREATMENT: {
          const waterVolume = selections.waterVolume;
          if (!waterVolume) return [];

          const tags = [];
          const waterUnitName = toPlainText(enrichedData.waterVolumeUnitName);

          tags.push({
            key: 'water-volume',
            label: `${t('treatment_water_volume_label')} : ${waterVolume}${waterUnitName ? ` ${waterUnitName}` : ''}`,
            pinned: true,
          });

          // Product doses
          const products = selections.products || [];
          const productNames = enrichedData.productNames || {};
          const productDoseUnitNames = enrichedData.productDoseUnitNames || {};

          products.forEach((product) => {
            if (product.id && product.dose) {
              const name = productNames[product.id] || product.name || product.id;
              const doseUnit = toPlainText(
                productDoseUnitNames[product.id] || product.unitName
              );
              tags.push({
                key: `product-${product.id}`,
                label: `${name} : ${product.dose}${doseUnit ? ` ${doseUnit}` : ''}`,
              });
            }
          });

          return tags;
        }

        default:
          return [];
      }
    }, [step, selections, enrichedData, t]);

    // Don't render anything if no selections
    if (displayValues.length === 0) {
      return null;
    }

    // Pinned items (e.g. water volume) are always visible and don't count against maxVisible.
    // Only non-pinned items are subject to the "+N" overflow.
    const pinnedItems = displayValues.filter((item) => item.pinned);
    const countedItems = displayValues.filter((item) => !item.pinned);
    const visibleItems = [...pinnedItems, ...countedItems.slice(0, maxVisible)];
    const remainingItems = countedItems.slice(maxVisible);
    const hasMore = remainingItems.length > 0;

    const renderTag = (item) => {
      const tagContent = (
        <Tag className="max-w-[200px] sm:max-w-[260px] overflow-hidden text-ellipsis whitespace-nowrap block">
          <HtmlContent html={item.label} />
        </Tag>
      );

      return <Tooltip title={<HtmlContent html={item.label} />}>{tagContent}</Tooltip>;
    };

    const popoverContent = (
      <div className="flex flex-wrap gap-1" style={{ maxWidth: '300px' }}>
        {remainingItems.map((item) => (
          <Tag key={item.key}>
            <HtmlContent html={item.label} />
          </Tag>
        ))}
      </div>
    );

    return (
      <div className="mt-2 flex flex-wrap items-center gap-1">
        {visibleItems.map((item) => (
          <span key={item.key}>{renderTag(item)}</span>
        ))}
        {hasMore && (
          <Popover content={popoverContent} trigger={['hover', 'click']} placement="bottom">
            <Tag className="cursor-pointer">
              +{remainingItems.length}
            </Tag>
          </Popover>
        )}
      </div>
    );
  }
);

SelectedValuesTags.displayName = 'SelectedValuesTags';

export default SelectedValuesTags;
